import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import Link from 'next/link'
import { Language } from '@/lib/enums/language'
import { createResource, i18next, useResource } from '@/lib/i18n'
import { observer } from 'mobx-react'
import { initStore } from '@/store'
import SwitchLang from './switch-lang'
import { MenuType } from '@/lib/constant'
import { eventLogProducer } from '@/lib/event-logger'
import { Action } from '@@/action_pb'
import { useWindowSize } from '@/lib/use-hooks/use-window-size'
import { getItem, resolveURL, shimmer, toBase64 } from '@/lib/utils'
import { OtherUrl } from '@/lib/enums/pages'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Autoplay } from 'swiper'
import { MenuPCPopup } from './menu-popup'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuItem from '@mui/material/MenuItem'
import ExpandMore from '@mui/icons-material/ExpandMore'
SwiperCore.use([Autoplay])

const resource = createResource({
  en: {
    home: 'Home',
    pintar: 'Pintar Score',
    product: 'Product',
    faq: 'FAQs',
    about: 'About Us',
    edu: 'Education',
    career: 'Career',
    shop: 'Pintar Shop',
    event: 'Event',
    contact: 'Contact Us',
    support: 'Support',
  },
  id: {
    home: 'Home',
    pintar: 'Skor Pintar',
    product: 'Produk',
    faq: 'FAQs',
    about: 'Tentang Kami',
    edu: 'Edukasi',
    career: 'Karir',
    shop: 'Pintar Shop',
    event: 'Acara',
    contact: 'Hubungi Kami',
    support: 'Dukungan',
  },
})

type Params = {
  hideSwitch?: boolean
}

const TopNavigation = (params: Params) => {
  const h5PointarShop = resolveURL(OtherUrl.PINTARSHOP)
  const { width } = useWindowSize()
  const router = useRouter()
  const isFromMobile = width < 600
  const { t } = useResource(resource)
  const [language, setLanguage] = useState(router.locale === 'id' ? 'ID' : 'EN')

  const faqLang = useMemo(() => (language === 'ID' ? 'id-id' : 'en-gb'), [language])

  const baseMenuList = useMemo(
    () => [
      {
        text: 'home',
        route: `/`,
        type: MenuType.HOME,
      },
      {
        text: 'pintar',
        route: `/pintar-score`,
        type: MenuType.PINTARSCORE,
      },
      {
        text: 'product',
        route: `/product`,
        type: MenuType.PRODUCT,
      },
      {
        text: 'about',
        route: `/about-us`,
        type: MenuType.ABOUT,
      },
      {
        text: 'career',
        route: `/career`,
        type: MenuType.CAREER,
      },
      {
        text: 'edu',
        route: `/education`,
        type: MenuType.EDU,
      },
      {
        text: 'event',
        route: `/event`,
        type: MenuType.EVENT,
      },
      {
        text: 'support',
        children: [
          {
            text: 'faq',
            route: `https://help.kreditpintar.com/hc/${faqLang}`,
            type: MenuType.FAQ,
          },
          {
            text: 'contact',
            route: `https://help.kreditpintar.com/hc/${faqLang}/requests/new`,
            type: MenuType.CONTACT,
          },
        ],
      },
    ],
    [faqLang],
  )

  const [showDrawer, setShowDrawer] = useState(false)
  const [menuList, setMenuList] = useState(baseMenuList)
  const [tkbList, setTkbList] = useState<Array<{ key: string; value: string | number }>>([])
  const [expanded, setExpanded] = useState<MenuType | null>(null)

  useEffect(() => {
    setTkbList((getItem('tkbList') as Array<{ key: string; value: string | number }> | null) || [])
  }, [])

  const eventClick = () => {
    eventLogProducer.push({
      action: Action.HEADERMENUBUTTONCLICK,
    })
  }

  useEffect(() => {
    const flag = menuList.some((item) => item.type === MenuType.SHOP)
    if (width > 0 && width <= 550 && !flag) {
      menuList.splice(2, 0, {
        text: 'shop',
        route: h5PointarShop,
        type: MenuType.SHOP,
      })
    }
    setMenuList(menuList)
  }, [router.locale, width])

  const isHighlight = (route: string) => {
    if (route === '/') {
      return route === router.route
    } else {
      return router.route.includes(route)
    }
  }

  const eventChange = (type: MenuType) => {
    eventLogProducer.push({
      action: Action.HEADERNAVIGATIONAREALINKCLICK,
      extra: { targetLinkName: type },
    })
  }

  const handleChange = (event: { target: { value: string } }) => {
    const value = event.target.value
    initStore().ConfigStore.setLanguage(value)
    i18next.changeLanguage(Language[value])
    setLanguage(value)
    setMenuList(baseMenuList)
    // 目前faq的question放在wp上，切换语言之后无法刷新，所以在faq详情页切换语言之后跳到faq列表页面
    if (router.asPath.match(/\/faq\/\w+/)) {
      router.push('/faq', '', {
        locale: language === 'ID' ? 'en-us' : 'id',
      })
    } else {
      router.push(router.asPath, '', {
        locale: language === 'ID' ? 'en-us' : 'id',
      })
    }
  }

  useEffect(() => {
    setMenuList(baseMenuList)
  }, [language])

  const toggleMenu = useCallback((menu: MenuType) => {
    setExpanded((prev) => (prev === menu ? null : menu))
  }, [])

  return (
    <HorizontalWrapper>
      <div>
        <nav className="nav-container" role="navigation">
          <div className="content">
            <div className="content__left">
              <Image
                className="logo"
                onClick={() => router.push('/')}
                src={'/images/home/logo.png'}
                width={isFromMobile ? 115 : 212}
                height={isFromMobile ? 22 : 42}
                quality={85}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(isFromMobile ? 115 : 212, isFromMobile ? 22 : 42),
                )}`}
                alt="KreditPintar"
              />
              <div className="subtitle divide"></div>
              <Swiper
                loop
                autoplay={{
                  delay: 3000,
                }}
                speed={600}
                direction="vertical"
                className="custom-swiper"
              >
                {tkbList?.map((item, index) => {
                  return (
                    <SwiperSlide key={`swiper-slide-${index}`}>
                      <div className="subtitle">
                        {item.key}: {item.value}%
                      </div>
                    </SwiperSlide>
                  )
                })}
              </Swiper>
            </div>
            {showDrawer ? (
              <Image
                width={18}
                height={18}
                quality={85}
                placeholder="empty"
                className="mobile-menu"
                src={'/images/home/close-icon.png'}
                alt="mobile-close"
                onClick={() => setShowDrawer(!showDrawer)}
              />
            ) : (
              <div onClick={() => eventClick()}>
                <Image
                  width={18}
                  height={18}
                  quality={85}
                  placeholder="empty"
                  className="mobile-menu"
                  src={'/images/home/menu.png'}
                  alt="mobile-menu"
                  onClick={() => setShowDrawer(!showDrawer)}
                />
              </div>
            )}
            <div className="content__right">
              <div className="content__right__menu">
                {menuList.map(({ text, route, type, children }, i) => (
                  <nav key={`nav-${i}`} className="content__right__nav">
                    {children?.length ? (
                      <ClickAwayListener onClickAway={() => (!showDrawer ? setExpanded(null) : null)}>
                        <div style={{ position: 'relative' }} className="content__right__nav">
                          <div className="content__right__menu__item" onClick={() => toggleMenu(type)}>
                            {t(text as any)}
                            <RotatingIconWrapper expanded={expanded === type} style={{ marginLeft: '4px' }}>
                              <ExpandMore style={{ color: '#c2c2c2' }} />
                            </RotatingIconWrapper>
                          </div>
                          <MenuPCPopup visible={expanded === type} closeExpanded={() => setExpanded(null)}>
                            {children.map((item, index) => (
                              <MenuItem key={`menu-item-${index}`}>
                                <div>
                                  <Link
                                    href={item.route}
                                    locale={Language[language] === 'id' ? 'id' : 'en-us'}
                                    onClick={() => eventChange(item.type)}
                                    className={
                                      isHighlight(item.route)
                                        ? 'content__right__menu__item--bold'
                                        : 'content__right__menu__item--child'
                                    }
                                  >
                                    {t(item.text as any)}
                                  </Link>
                                </div>
                              </MenuItem>
                            ))}
                          </MenuPCPopup>
                        </div>
                      </ClickAwayListener>
                    ) : (
                      <Link
                        href={route}
                        locale={Language[language] === 'id' ? 'id' : 'en-us'}
                        onClick={() => eventChange(type)}
                        className={
                          isHighlight(route) ? 'content__right__menu__item--bold' : 'content__right__menu__item'
                        }
                      >
                        {t(text as any)}
                      </Link>
                    )}
                  </nav>
                ))}
              </div>
              <div className="content__right__languages">
                {!params.hideSwitch && <SwitchLang language={language} handleChange={handleChange} />}
              </div>
            </div>
          </div>
          {showDrawer && (
            <div className="nav-menu">
              {menuList.map(({ text, route, type, children }) => (
                <nav key={`nav-${text}`}>
                  {children?.length ? (
                    <>
                      <div className="menu__item" onClick={() => toggleMenu(type)}>
                        {t(text as any)}
                        <RotatingIconWrapper expanded={expanded === type} style={{ marginLeft: '4px' }}>
                          <ExpandMore style={{ color: '#C2C2C2' }} />
                        </RotatingIconWrapper>
                      </div>
                      {expanded === type &&
                        children.map((item, index) => (
                          <Link
                            key={`link-${index}`}
                            href={item.route}
                            locale={Language[language] === 'id' ? 'id' : 'en-us'}
                            onClick={() => eventChange(item.type)}
                            className={`menu__item submenu__item ${isHighlight(route) ? 'menu__item--bold' : ''}`}
                          >
                            {t(item.text as any)}
                          </Link>
                        ))}
                    </>
                  ) : (
                    <Link
                      href={route}
                      locale={Language[language] === 'id' ? 'id' : 'en-us'}
                      onClick={() => eventChange(type)}
                      className={isHighlight(route) ? 'menu__item menu__item--bold' : 'menu__item'}
                    >
                      {t(text as any)}
                    </Link>
                  )}
                </nav>
              ))}
              <div className="mobile_languages">
                {!params.hideSwitch && <SwitchLang language={language} handleChange={handleChange} />}
              </div>
            </div>
          )}
        </nav>
      </div>
    </HorizontalWrapper>
  )
}

const HorizontalWrapper = styled.nav`
  @media only screen and (max-width: 1000px) {
    .nav-container {
      position: fixed;
      height: 62px;
      width: 100%;
      z-index: 999;
      margin: 0 auto;
      box-shadow: 0 0 1px 0 #cfd2e3;
      transition: all 0.25s;
      padding: 0 24px;
      box-sizing: border-box;
      background-color: #fff;
      .content {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .content__left {
          display: flex;
          justify-content: center;
          align-items: center;
          .divide {
            width: 1px;
            height: 6px;
            font-size: 12px;
            background-color: #bbb;
            margin: 0 8px;
            margin-top: 5px;
          }
          .subtitle {
            font-size: 10px;
            font-family: Roboto-Regular, Roboto;
            font-weight: 400;
            color: #999999;
            line-height: 11px;
            white-space: nowrap;
            margin-top: 8px;
          }
          .custom-swiper {
            height: 22px;
            line-height: 22px;
          }
        }
        .mobile-menu {
          width: 18px;
          height: 18px;
        }
        .content__right {
          display: none;
        }
      }
      .nav-menu {
        position: fixed;
        left: 0;
        width: 100%;
        height: 100%;
        background: #fff;
        z-index: 1;
        transition: all 0.5s;
        .mobile_languages {
          margin-left: 24px;
        }
        .menu__item {
          display: flex;
          align-items: center;
          width: 100%;
          height: 44px;
          line-height: 44px;
          padding: 0 24px;
          font-size: 16px;
          font-family: Roboto-Regular, Roboto;
          font-weight: 400;
          color: #757575;
          white-space: nowrap;
        }
        .menu__item--bold {
          color: #1cc812;
        }

        .submenu__item {
          font-size: 14px;
        }
      }
    }
  }
  @media only screen and (min-width: 1000px) {
    .nav-container {
      position: fixed;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      height: 100px;
      width: 100%;
      z-index: 999;
      transition: all 0.25s;
      padding: 0 120px;
      box-sizing: border-box;
      background-color: #fff;
      box-shadow: 0px 8px 32px 0px rgba(93, 102, 120, 0.06);
      .content {
        height: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        .content__left {
          display: flex;
          justify-content: center;
          align-items: center;
          .logo {
            cursor: pointer;
          }
          .divide {
            width: 2px;
            height: 12px;
            font-size: 12px;
            background-color: #bbb;
            margin: 0 10px;
          }
          .subtitle {
            font-size: 14px;
            font-family: Roboto-Regular, Roboto;
            font-weight: 400;
            color: #999;
            white-space: nowrap;
          }
          .custom-swiper {
            height: 40px;
            line-height: 40px;
          }
        }
        .mobile-menu {
          display: none !important;
        }
        .content__right {
          height: 100%;
          display: flex;
          justify-content: space-between;
          .content__right__nav {
            position: relative;
            display: inline-block;
          }
          .content__right__nav:not(:last-child) {
            margin-right: 35px;
          }
          &__menu {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 30px;
            &__item {
              font-size: 16px;
              font-weight: 400;
              color: rgba(0, 0, 0, 0.54);
              text-decoration: none;
              transition: all 0.25s;
              letter-spacing: 0.26px;
              height: 100%;
              cursor: pointer;
              display: flex;
              align-items: center;
              &:hover {
                transition: background-color 0.2s linear;
                color: #333;
              }
            }
            &__item--bold {
              color: #1cc812;
              font-size: 16px;
              font-family: Roboto-Medium, Roboto;
              font-weight: 500;
              transition: all 0.25s;
              letter-spacing: 0.26px;
              height: 100%;
              cursor: pointer;
              display: flex;
              align-items: center;
              text-decoration: none;
              &:not(:last-child) {
                margin-right: 35px;
              }
            }
            &__item--child {
              &:hover {
                color: #1cc812;
              }
            }
          }
          &__languages {
            cursor: pointer;
            height: 100%;
            display: flex;
            align-items: center;
            .language-icon {
              width: 19px;
              height: 14px;
            }
            &__display {
              height: 38px;
              width: 115px;
              box-sizing: border-box;
              border-radius: 19px;
              display: flex;
              justify-content: center;
              align-items: center;
              font-family: inherit;
              font-size: 14px;
              font-weight: normal;
              font-stretch: normal;
              font-style: normal;
              letter-spacing: 0.22px;
              text-align: center;
              border: solid 2px rgba(255, 255, 255, 0.27);
              color: rgba(255, 255, 255, 0.7);
              transition: background-color 0.2s linear;
              position: relative;

              &:hover {
                transition: background-color 0.2s linear;
                background-color: rgba(255, 255, 255, 0.27);
              }
            }
          }
        }
      }
      .nav-menu {
        display: none;
      }
    }
  }
`

const RotatingIconWrapper = styled('div')<{ expanded: boolean }>(({ expanded }) => ({
  display: 'inline-flex',
  cursor: 'pointer',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease-in-out',
}))

export default observer(TopNavigation)
